import axios from "axios";
import { getToken } from "@/lib/tokenStore";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const imgSrc = (img) => {
  if (!img) return null;
  if (img.url) return img.url;
  if (img.path) return `${API}/images/${img.path}`;
  return null;
};

export const primaryImage = (profile) => {
  const imgs = profile?.images || [];
  return imgs.find((i) => i.is_primary) || imgs[0] || null;
};

export const errMsg = (e, fallback = "Something went wrong") =>
  e?.response?.data?.detail
    ? typeof e.response.data.detail === "string"
      ? e.response.data.detail
      : JSON.stringify(e.response.data.detail)
    : fallback;

export const fmtMoney = (n) =>
  n === null || n === undefined
    ? "—"
    : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const CATEGORIES = [
  { value: "lifestyle", label: "Lifestyle" },
  { value: "high_fashion", label: "High Fashion" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "sportswear", label: "Sportswear" },
  { value: "swimwear", label: "Swimwear" },
  { value: "body_part", label: "Body Part" },
  { value: "fitness_athletic", label: "Fitness & Athletic" },
  { value: "editorial", label: "Editorial" },
];

export const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

export const USAGE_TYPES = [
  { value: "ecommerce", label: "E-commerce product imagery" },
  { value: "lifestyle", label: "Lifestyle content" },
  { value: "body_part", label: "Body part modelling (watches, jewellery)" },
  { value: "sportswear", label: "Sportswear campaign" },
  { value: "fitness_athletic", label: "Fitness & athletic (sports, nutrition)" },
  { value: "swimwear", label: "Swimwear campaign" },
  { value: "editorial_magazines", label: "Editorial & magazines" },
  { value: "high_fashion", label: "High fashion campaign" },
  { value: "advertising", label: "Commercial advertising" },
];

export const usageLabel = (v) => USAGE_TYPES.find((u) => u.value === v)?.label || v;

export const MEDIA_CHANNELS = [
  { value: "own_online_store", label: "Own online store", hint: "Your own website / e-commerce app" },
  { value: "marketplace_listings", label: "Marketplace listings", hint: "Amazon, Zalando, ASOS etc." },
  { value: "social_organic", label: "Social media (organic)", hint: "Your own feeds, unpaid posts" },
  { value: "social_paid_ads", label: "Social media (paid ads)", hint: "Boosted posts, ad campaigns" },
  { value: "digital_display_ads", label: "Digital display ads", hint: "Banner / programmatic advertising" },
  { value: "email_marketing", label: "Email marketing", hint: "Newsletters, campaigns" },
  { value: "print_catalogue", label: "Print / catalogue", hint: "Printed lookbooks, catalogues" },
  { value: "lookbooks", label: "Lookbooks", hint: "Digital or printed lookbooks" },
  { value: "editorial_magazines", label: "Editorial & magazines", hint: "Magazine features, editorial spreads" },
  { value: "ooh_billboards", label: "Out-of-home / billboards", hint: "Billboards, transit, retail displays" },
];

export const channelLabel = (v) => MEDIA_CHANNELS.find((c) => c.value === v)?.label || v;

export const BANNABLE_CATEGORIES = [
  { value: "swimwear", label: "Swimwear" },
  { value: "lingerie", label: "Lingerie" },
  { value: "alcohol", label: "Alcohol brands" },
  { value: "gambling", label: "Gambling & betting" },
  { value: "fur_leather", label: "Fur & animal products" },
  { value: "tobacco", label: "Tobacco & vaping" },
];

export const bannableLabel = (v) => BANNABLE_CATEGORIES.find((c) => c.value === v)?.label || v;

export const validatePassword = (pw) => {
  if (!pw || pw.length < 6) return "Password must be at least 6 characters";
  if (!/[A-Z]/.test(pw)) return "Password must contain at least one capital letter";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Password must contain at least one symbol (e.g. ! @ # $ %)";
  return null;
};

export const COMPANY_SIZES = [
  { value: "startup", label: "Startup (1-10 people)" },
  { value: "small", label: "Small (11-50)" },
  { value: "medium", label: "Medium (51-250)" },
  { value: "large", label: "Large (251-1000)" },
  { value: "enterprise", label: "Enterprise (1000+)" },
];
